import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { PropertySearchResult, SearchFilters } from '../types/search';
import { searchService } from '../services/searchApi';

interface SearchContextType {
  // Search state
  searchResults: PropertySearchResult[];
  searchLoading: boolean;
  searchError: string | null;
  hasSearched: boolean;
  
  // Search filters
  searchFilters: SearchFilters;
  
  // Search actions
  performSearch: (filters: SearchFilters) => Promise<void>;
  clearSearch: () => void;
  updateFilters: (filters: Partial<SearchFilters>) => void;
  
  // Search options
  rentalTypes: Array<{ id: string; icon: string; label: string }>;
  durationOptions: Array<{ value: string; label: string; months: number }>;
  loadSearchOptions: () => Promise<void>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchResults, setSearchResults] = useState<PropertySearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  
  const [rentalTypes, setRentalTypes] = useState<Array<{ id: string; icon: string; label: string }>>([]);
  const [durationOptions, setDurationOptions] = useState<Array<{ value: string; label: string; months: number }>>([]);

  const searchRequestSeq = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const performSearch = useCallback(async (filters: SearchFilters) => {
    const seq = ++searchRequestSeq.current;
    abortControllerRef.current?.abort();
    const ac = new AbortController();
    abortControllerRef.current = ac;

    setSearchLoading(true);
    setSearchError(null);
    setSearchFilters(filters);
    
    try {
      const response = await searchService.searchProperties(filters as any, { signal: ac.signal });
      if (seq !== searchRequestSeq.current) {
        return;
      }
      // Keep backend ranking order as-is to avoid client-side reordering drift.
      setSearchResults(response.hits || []);
      setHasSearched(true);
      
      // Scroll to product grid only when user explicitly clicks search button
      // Check if we should scroll (only scroll on user click, not on tab auto-switch)
      const shouldScroll = filters.search_query || filters.property_types || filters.min_duration;
      
      if (shouldScroll) {
        setTimeout(() => {
          const productGrid = document.getElementById('product-grid');
          if (productGrid) {
            const rect = productGrid.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = rect.top + scrollTop - 80; // 80px offset from top
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        return;
      }
      console.error('❌ Search error:', error);
      if (seq === searchRequestSeq.current) {
        setSearchError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi tìm kiếm');
        setSearchResults([]);
      }
    } finally {
      if (seq === searchRequestSeq.current) {
        setSearchLoading(false);
      }
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchError(null);
    setHasSearched(false);
    setSearchFilters({});
  }, []);

  const updateFilters = useCallback((newFilters: Partial<SearchFilters>) => {
    setSearchFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const loadSearchOptions = useCallback(async () => {
    try {
      const response = await searchService.getSearchOptions();
      setRentalTypes(response.rental_types);
      setDurationOptions(response.duration_options);
    } catch (error) {
      console.error('Error loading search options:', error);
    }
  }, []);

  const value: SearchContextType = {
    searchResults,
    searchLoading,
    searchError,
    hasSearched,
    searchFilters,
    performSearch,
    clearSearch,
    updateFilters,
    rentalTypes,
    durationOptions,
    loadSearchOptions,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};